(ns client-portal.client.reports.individual.views
  (:require [clojure.string :as s]
            [client-portal.client.layout :as l]
            [client-portal.client.reports.individual.data :as data]
            [client-portal.client.reports.components.category-score-gauge-with-line-chart :refer [category-score-gauge-with-line-chart]]
            [client-portal.client.reports.components.key-metrics-stacked-bar-chart :refer [key-metrics-stacked-bar-chart]]
            [client-portal.client.reports.components.day-parts-toggle-group :refer [day-parts-toggle-group]]
            [client-portal.client.reports.components.table :refer [table]]
            [client-portal.client.voice-insights.empty-state :as empty-state]
            [shared.core.components.components :as c]
            [shared.core.amelia-light.components.components :as alc]
            [shared.core.reactive :as re]
            [shared.core.style :as style]
            [shared.core.utils.currency :as currency]
            [shared.core.utils.sql :as sql]
            [shared.core.util :as util]
            [client-portal.client.voice-insights.charts.views :as charts]
            [client-portal.client.voice-insights.charts.data :as charts.d]
            [shared.core.components.recharts.util :as recharts.util]
            ["react-markdown" :default ReactMarkdown]
            ["remark-gfm" :default remarkGfm]))

(declare component-grid)

(defn- format-value-card-value [{:keys [value-type country-code template value]}]
  (try
    (let [formatted (condp = value-type
                      "currency" (currency/currency-integer->readable-currency (or country-code "US") value)
                      "percent" (str (util/to-precision-num (* value 100) :precision 2) "%")
                      "text" (str value)
                      (util/readable-number value))
          v (if (not-empty template)
              (s/replace template "{value}" (str formatted))
              formatted)]
      (if (and (not (string? v)) (NaN? v)) value v))
    (catch :default _
      value)))

(defn- value-card-values
  "The card's primary and secondary values, read from a result `row` by NAME. A
   row's key order doesn't survive the trip here (its jsonb round trip re-orders
   the keys), so the primary value is the column aliased `value` and the
   secondary is whichever column is left — never `first`/`second` of the row."
  [row]
  (let [value-key (or (some #(when (= "value" (name %)) %) (keys row))
                      (first (keys row)))]
    [(get row value-key)
     (first (vals (dissoc row value-key)))]))

(defn value-card [{:keys [title description value-type color template secondary-value-config country-code data]}]
  (let [{secondary-value-type :value-type
         secondary-value-color :color
         secondary-template :template} secondary-value-config
        data? (not-empty (util/when-not-loading data))
        row (when data?
              (first data))
        [value secondary-value] (value-card-values row)]
    [c/interactive
     alc/card {:title title
               :subtitle description
               :style {:height "100%"}}
     (cond
       (util/loading? data)
       [alc/up-loader-with-label {:label "Loading Data..."
                                  :style {:padding 60}}]

       value
       [:div {:style {:flex 1
                      :display :flex
                      :flex-direction :column
                      :align-items :center
                      :justify-content :center
                      :gap 6
                      :text-align :center
                      :padding (if secondary-value
                                 10
                                 "10px 10px 20px")}}
        [:div {:style {:font-size 50
                       :line-height 1.2
                       :font-weight style/font-medium-weight
                       :color (or color (style/alc-color "Text/primary"))
                       :text-align :center
                       :overflow :hidden
                       :text-overflow :ellipsis
                       :max-width "100%"}}
         (format-value-card-value {:value-type value-type
                                   :country-code country-code
                                   :template template
                                   :value value})]
        (when secondary-value
          [:div {:style {:font-size 20
                         :line-height 1.2
                         :font-weight style/font-regular-weight
                         :color (or secondary-value-color (style/alc-color "Text/secondary"))
                         :text-align :center
                         :overflow :hidden
                         :text-overflow :ellipsis
                         :max-width "100%"}}
           (format-value-card-value {:value-type secondary-value-type
                                     :country-code country-code
                                     :template secondary-template
                                     :value secondary-value})])]

       :else
       [empty-state/no-data-card {:style {:padding 20}}])]))

(defn query-card
  "The SQL a component actually ran, in place of the component itself. Same card
   shell as `value-card`/`chart-card` so swapping between them doesn't move the grid."
  [{:keys [title query]}]
  [alc/card {:title (or (not-empty title) "Query")
             :subtitle "Executed query"
             :style {:height "100%"}}
   [:pre {:style {:background-color (style/alc-color "Background/secondary")
                  :border (str "1px solid " (style/alc-color "Border/divider_secondary"))
                  :border-radius 8
                  :color (style/alc-color "Text/primary")
                  :font-family "ui-monospace, SFMono-Regular, Menlo, monospace"
                  :font-size 12
                  :line-height 1.5
                  :margin 0
                  :max-height 600
                  :overflow :auto
                  :padding 12
                  :white-space :pre
                  :width "100%"}}
    (sql/format-query query)]])

(defn report-tabs [raw-config default-filter-values]
  (let [raw-cmp-config (get raw-config "configuration")
        tabs (get raw-cmp-config "tabs")
        cmp-config (util/->kebab-case-keywords raw-config)]
    [alc/up-tabs (assoc cmp-config
                   :style (merge {:height "100%"}
                                 (:style cmp-config))
                   :list-style (merge {:background-color (style/alc-color "Background/primary")
                                       :position :sticky
                                       :top 60
                                       :left 0}
                                      (:list-style cmp-config))
                   :tabs (mapv (fn [tab]
                                 (assoc (util/->kebab-case-keywords tab)
                                   :content [[:div {:style {:display :flex
                                                            :align-items :center
                                                            :justify-content :center
                                                            :padding 40
                                                            :height "100%"
                                                            :width "100%"}}
                                              [component-grid {:configuration {"content" (get tab "content")}
                                                               :default-filter-values default-filter-values}]]]))
                               tabs))]))

(defn grid-cmp [_]
  (let [report-id- (re/subscribe [:reports/current-report-id-])
        admin?- (re/subscribe [:account/admin-role?-])
        hover?* (re/ratom false)
        show-query?* (re/ratom false)]
    (fn [{:keys [raw-cmp-config view-only? report-id default-filter-values]}]
      (let [cmp-config (util/->kebab-case-keywords raw-cmp-config)]
        (when-let [cmp-type (:component-type cmp-config)]
          (let [report-id (or report-id @report-id-)
                cmp-content (get raw-cmp-config "content")
                cmp-args (cond-> (:configuration cmp-config)
                                 view-only?
                                 (assoc :on-click #()
                                        :on-value-change #())
                                 ;; Report-level default filter values ride along so a chart's
                                 ;; session double-click can seed the drill-in query params
                                 ;; (filling only keys the current route doesn't already set).
                                 default-filter-values
                                 (assoc :default-filter-values default-filter-values))
                content (into (condp = cmp-type
                                "chart" [charts/chart-from-config cmp-args]
                                "chart-card" (let [sankey? (= (:chart-type cmp-args) "Sankey")
                                                   chart-data (cond-> (get-in raw-cmp-config ["configuration" "data"])
                                                                      ;; Sankey needs {nodes links}; build it from the flat query rows
                                                                      ;; (keyword-keyed) using the authored source/target/value columns.
                                                                      sankey?
                                                                      (#(recharts.util/build-sankey-data
                                                                          {:data (mapv util/->kebab-case-keywords %)
                                                                           :source-key (keyword (:source-key cmp-args))
                                                                           :target-key (keyword (:target-key cmp-args))
                                                                           :value-key (keyword (:value-key cmp-args))}))
                                                                      (:show-time-period-toggle? cmp-args)
                                                                      (charts.d/->formatted-chart-data :grouping-key (:time-period-key cmp-args)))
                                                   config-overrides (:config-overrides cmp-args)]
                                               [charts/chart-card (assoc cmp-args
                                                                    ;; Only a local key (per-chart form state) — report data is
                                                                    ;; always server-resolved, so never let the card self-fetch it.
                                                                    :chart-id (or (:chart-id cmp-args)
                                                                                  (str report-id
                                                                                       "-"
                                                                                       (if-let [title (:title cmp-args)]
                                                                                         (util/->kebab-case-string title)
                                                                                         (util/random-uuid-string))))
                                                                    :self-load? false
                                                                    :data chart-data
                                                                    ;; Raw: keyed by the values it labels, which kebab-casing would mangle.
                                                                    :labels (get-in raw-cmp-config ["configuration" "labels"])
                                                                    :config-overrides (cond-> config-overrides
                                                                                              (:data-keys-config config-overrides)
                                                                                              (assoc :data-keys-config (get-in raw-cmp-config ["configuration"
                                                                                                                                               "configOverrides"
                                                                                                                                               "dataKeysConfig"]))))])
                                "component-grid" [component-grid (assoc raw-cmp-config :default-filter-values default-filter-values)]
                                "markdown" [:div {:class-name "markdown-body"
                                                  :style {:color (style/alc-color "Text/primary")
                                                          :font-size 16
                                                          :line-height 1.4
                                                          :width "100%"}}
                                            [:> ReactMarkdown
                                             (merge {:remarkPlugins [remarkGfm]}
                                                    cmp-args)]]
                                "section-header" [l/section-header (assoc cmp-args :data (get-in raw-cmp-config ["configuration" "data"]))]
                                "tabs" [report-tabs raw-cmp-config default-filter-values]
                                "value-card" [value-card (assoc cmp-args :data (get-in raw-cmp-config ["configuration" "data"]))]
                                "table" [table (assoc cmp-args :data (get-in raw-cmp-config ["configuration" "data"]))]
                                "category-score-gauge-with-line-chart" [category-score-gauge-with-line-chart cmp-args]
                                "key-metrics-stacked-bar-chart" [key-metrics-stacked-bar-chart cmp-args]
                                "day-parts-toggle-group" [:div {:style {:height :auto}}
                                                          [day-parts-toggle-group cmp-args]]
                                [(keyword cmp-type) cmp-args])
                              (if (coll? cmp-content)
                                (mapv (fn [conf] [grid-cmp {:raw-cmp-config conf
                                                            :default-filter-values default-filter-values}]) cmp-content)
                                cmp-content))]
            (when (not-empty content)
              ;; `:query` is the actually-executed SQL, attached by the resolver for
              ;; admins only (never present for non-admins). Hover-reveal the query
              ;; actions when it's there.
              (let [query (:query cmp-args)
                    query-actions? (and @admin?- (not-empty query))
                    ;; Once the query is showing, keep the actions up without a hover —
                    ;; otherwise the only way back to the card is hidden.
                    showing-query? (and query-actions? @show-query?*)]
                [c/interactive
                 :div {:style {:flex 1
                               :position :relative}
                       :hover?* hover?*}
                 (when (and query-actions? (or @hover?* showing-query?))
                   [:div {:style {:display :flex
                                  :align-items :center
                                  :gap 8
                                  :position :absolute
                                  :top 19
                                  ;; Clear the chart-card's view-mode toggle, which the
                                  ;; query card doesn't have.
                                  :right (if (and (= cmp-type "chart-card")
                                                  (not showing-query?)
                                                  (not (false? (:show-view-mode-toggle? cmp-args))))
                                           132 19)
                                  :z-index 2}}
                    [alc/tooltip
                     {:target-content [alc/up-button {:icon-type :CopyIcon
                                                      :on-click #(re/dispatch-sync [:core/copy-to-clipboard query {:notify? true}])}]
                      :tooltip-content "Copy query"}]
                    [alc/tooltip
                     {:target-content [alc/up-button {:icon-type (if showing-query? :CardsIcon :CodeIcon)
                                                      :on-click #(swap! show-query?* not)}]
                      :tooltip-content (if showing-query? "Show card" "Show query")}]])
                 (if showing-query?
                   [query-card {:title (:title cmp-args)
                                :query query}]
                   content)]))))))))

(defn component-grid [{:keys [configuration no-content-message view-only? report-id default-filter-values root-id]}]
  (let [content (get configuration "content")
        ;; Read once at the report root; threaded down so nested charts inherit it.
        default-filter-values (or default-filter-values (get configuration "defaultFilterValues"))]
    (if (empty? content)
      [empty-state/no-data-card
       {:text (or no-content-message "No content")
        :material-icon-type :inventory_2}]
      (into [:div {:id root-id
                   :style {:flex 1
                           :display :flex
                           :flex-direction :column
                           :gap 12
                           :min-height "100%"
                           :width "100%"}}]
            (mapv (fn [row-content]
                    (->> row-content
                         (mapv (fn [raw-cmp-config] [grid-cmp {:raw-cmp-config raw-cmp-config
                                                               :view-only? view-only?
                                                               :report-id report-id
                                                               :default-filter-values default-filter-values}]))
                         (into [:div {:style {:flex "0 1 auto"
                                              :display :flex
                                              :gap 12
                                              :width "100%"}}])))
                  content)))))

(defn report-details [args]
  [component-grid (assoc args
                    :no-content-message "Report empty"
                    :root-id data/pdf-grid-root-id)])

(defn root [_]
  (let [scheduled-refresh* (atom nil)
        seeded-defaults* (atom nil)
        day-parts- (re/subscribe [:day-parts/by-current-concept-id-])
        qps- (re/subscribe [:router/current-query-params-])
        admin?- (re/subscribe [:account/admin-role?-])]
    (re/create-class
      {:component-did-mount (fn [this]
                              (re/dispatch [:reports/initialize-individual])
                              ;; Deep-link / refresh on the report session route
                              ;; (/reports/i/:report-id/:session-id): reopen the chat panel
                              ;; and load that session.
                              (let [{:keys [report-id session-id]} (re/props this)]
                                (when (not-empty session-id)
                                  (re/dispatch [:ai-agent/resume-report-chat {:report-id report-id
                                                                              :session-id session-id}]))))
       :component-will-unmount (fn [_]
                                 (re/dispatch [:reports/clear-refresh-timer])
                                 (re/dispatch [:layout/hide-panel-right])
                                 (reset! scheduled-refresh* nil))
       :reagent-render
       (fn [{:keys [report-id all-reports-button?]
             :or {all-reports-button? true}
             :as args}]
         (if (and (:dp @qps-) (or (util/loading? @day-parts-) (nil? @day-parts-)))
           [l/logged-in-with-header-nav-vi args
            [l/standard-content-container
             [empty-state/loading-report-card]]]
           (let [report @(re/subscribe [:reports/individual-data- report-id])
                 loading? (util/loading? report)
                 ready? (and (not loading?) (not (nil? report)) (seq report))
                 ;; Fetched ahead of the report so the chrome resolves without waiting on
                 ;; its queries. Nil until it lands, which holds every configurable
                 ;; filter back rather than showing one the report turns out to hide.
                 settings (util/when-not-loading @(re/subscribe [:reports/settings- report-id]))
                 ;; The report's own setting decides, with nothing overriding it — a ?lid
                 ;; in the URL is left alone but neither shows the control nor reaches the
                 ;; query when the report hides location.
                 filters (when settings (data/report-filters (:filters settings)))
                 target-concept-ids (:target-concept-ids settings)
                 ;; Owner or direct share — an organization share is read-only, and the
                 ;; edit-session endpoint enforces the same rule.
                 can-edit? (:can-edit (util/when-not-loading report))
                 refresh-seconds (get-in report [:configuration "refreshSeconds"])]
             (when (and ready? (not= report-id @seeded-defaults*))
               (reset! seeded-defaults* report-id)
               (re/dispatch [:reports/seed-default-filter-params report-id]))
             (when ready?
               (let [refresh-seconds (when (some-> refresh-seconds (pos?)) refresh-seconds)]
                 (when (not= refresh-seconds @scheduled-refresh*)
                   (reset! scheduled-refresh* refresh-seconds)
                   (if refresh-seconds
                     (re/dispatch [:reports/set-refresh-timer refresh-seconds])
                     (re/dispatch [:reports/clear-refresh-timer])))))
             [l/logged-in-with-header-nav-vi
              (merge args {:concept-dropdown? true
                           :location-dropdown? (boolean (:location filters))
                           :crew-type-radio? (boolean (:crew-type filters))
                           :timeframe-dropdown? (boolean (:date filters))}
                     (when (not-empty target-concept-ids)
                       {:concept-dropdown-config {:filter-concept-ids target-concept-ids}}))
              [l/standard-content-container {:id data/pdf-capture-element-id
                                             :title [:div {:style {:display :flex
                                                                   :justify-content :space-between}}
                                                     [:span {:style {:color :inherit}}
                                                      (:title (util/when-not-loading report))]
                                                     [:div {:data-html2canvas-ignore "true"
                                                            :style {:display :flex
                                                                    :align-items :center
                                                                    :gap 8}}
                                                      #_(when (and @admin?- ready?)
                                                          [alc/up-button {:icon-type :QuestionmarkCircleIcon
                                                                          :on-click #(re/dispatch [:ai-agent/open-report-chat {:report-id report-id}])}
                                                           "Ask a Question"])
                                                      (when ready?
                                                        (let [generating? @(re/subscribe [:reports/generating-pdf?-])]
                                                          [alc/up-button {:icon-type :DownloadIcon
                                                                          :loading? generating?
                                                                          :on-click #(re/dispatch [:reports/download-pdf {:title (:title (util/when-not-loading report))}])}
                                                           "Download PDF"]))
                                                      (when all-reports-button?
                                                        [alc/up-button {:icon-type :TxtListBulletedIcon
                                                                        :on-click #(re/dispatch [:reports/open-list])}
                                                         "All Reports"])
                                                      (when (and @admin?- ready? can-edit?)
                                                        [alc/up-call-to-action-button {:icon-type :EditIcon
                                                                                       :loading? @(re/subscribe [:reports/creating-edit-session?-])
                                                                                       :on-click #(re/dispatch [:reports/edit-report report-id])}
                                                         "Edit"])]]
                                             :description (:description (util/when-not-loading report))
                                             :sticky-header? true}
               (cond
                 (or loading? (nil? report)) [empty-state/loading-data-card]
                 (empty? report) [empty-state/no-data-card
                                  {:text "Report not found"
                                   :material-icon-type :inventory_2}]
                 :else [report-details report])]])))})))
